import { useState } from "react";
import toast from "react-hot-toast";
import { Receipt, Plus, CircleDollarSign } from "lucide-react";
import { useInvoices, useIssueInvoice, INVOICE_STATUS_LABEL, InvoiceStatus, invoiceBalance } from "@/api/invoices";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import CreateInvoiceModal from "@/pages/invoices/CreateInvoiceModal";
import RegisterPaymentModal from "@/pages/payments/RegisterPaymentModal";

const STATUS_STYLE: Record<InvoiceStatus, string> = {
  BORRADOR: "bg-gray-100 text-gray-600",
  EMITIDA: "bg-blue-100 text-blue-700",
  PARCIAL: "bg-amber-100 text-amber-700",
  PAGADA: "bg-green-100 text-green-700",
  VENCIDA: "bg-red-100 text-red-700",
  ANULADA: "bg-gray-200 text-gray-500",
};

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

export default function InvoicesSection({ clientId }: { clientId: string }) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { data, isLoading } = useInvoices({ clientId, pageSize: 50 });
  const issueInvoice = useIssueInvoice();
  const [showCreate, setShowCreate] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  const hasOutstanding = (data?.data ?? []).some((inv) => ["EMITIDA", "PARCIAL", "VENCIDA"].includes(inv.status));

  const handleIssue = async (id: string) => {
    try {
      await issueInvoice.mutateAsync(id);
      toast.success("Factura emitida.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Facturación</h2>
        <div className="flex items-center gap-3">
          {hasOutstanding && hasPermission("payments.create") && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-800"
              onClick={() => setShowPayment(true)}
            >
              <CircleDollarSign size={14} /> Registrar pago
            </button>
          )}
          {hasPermission("invoices.create") && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
              onClick={() => setShowCreate(true)}
            >
              <Plus size={14} /> Nueva factura
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {isLoading && <p className="text-sm text-gray-400">Cargando facturas...</p>}
        {!isLoading && (!data?.data || data.data.length === 0) && (
          <p className="text-sm text-gray-400">Este cliente aún no tiene facturas registradas.</p>
        )}
        {data?.data.map((invoice) => (
          <div key={invoice.id} className="flex items-center justify-between rounded-md border border-gray-100 p-3">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
                <Receipt size={15} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  N.° {invoice.invoiceNumber} · {currency.format(Number(invoice.total))}
                </p>
                <p className="text-xs text-gray-500">
                  Vence {invoice.dueDate}
                  {["PARCIAL", "VENCIDA"].includes(invoice.status) && (
                    <> · Saldo {currency.format(invoiceBalance(invoice))}</>
                  )}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {invoice.status === "BORRADOR" && hasPermission("invoices.approve") && (
                <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => handleIssue(invoice.id)}>
                  Emitir
                </button>
              )}
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[invoice.status]}`}>
                {INVOICE_STATUS_LABEL[invoice.status]}
              </span>
            </div>
          </div>
        ))}
      </div>

      {showCreate && <CreateInvoiceModal clientId={clientId} onClose={() => setShowCreate(false)} />}
      {showPayment && <RegisterPaymentModal clientId={clientId} onClose={() => setShowPayment(false)} />}
    </div>
  );
}
